import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiAlert, mdiCalendarRemove } from '@mdi/js';
import { toast } from 'react-toastify';
import BaseModal from './BaseModal';
import ReturnActionPanel from './ReturnActionPanel';
import { bookingApi } from '../api/booking';
import './CancellationModal.css';

const CancellationModal = ({ booking, onClose, onConfirm }) => {
  const [cancellationReason, setCancellationReason] = useState(
    'Sorry, I need to cancel due to unforeseen circumstances.'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('confirm'); // 'confirm' | 'return'
  const [isIssuingReturn, setIsIssuingReturn] = useState(false);

  // Credit-paid bookings need NO return step: cancelling re-grants the applied
  // credit automatically server-side, so there is no refund/credit decision to
  // make. Showing the panel anyway (with only a Close button) was confusing.
  const isCreditPaid = booking?.effectivePricing?.paidBy === 'platform_credit';
  const isPaidWithNoReturn = booking?.pricing?.paymentStatus === 'paid' &&
    (!booking.returnStatus || booking.returnStatus === 'none') &&
    !isCreditPaid;
  const isGuest = booking?.studentId === '000000000000000000000000';

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(booking.id, cancellationReason);
      if (isCreditPaid) {
        toast.success('Booking cancelled. The credit has been returned to the student\'s account.');
        onClose();
      } else if (isPaidWithNoReturn) {
        setStep('return');
        setIsSubmitting(false);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message || 'Could not cancel the booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleReturnConfirm = async (type, amount) => {
    setIsIssuingReturn(true);
    try {
      await bookingApi.issueReturn(booking.id, { type, amount });
      toast.success(type === 'refund' ? 'Refund issued successfully' : 'Credit granted to student');
      onClose();
    } catch (error) {
      toast.error('Could not issue return. Please try again.');
      setIsIssuingReturn(false);
    }
  };

  if (step === 'return') {
    return (
      <BaseModal
        isOpen
        onClose={onClose}
        title="Booking Cancelled"
        icon={mdiCalendarRemove}
        iconColor="var(--dl-color-danger)"
        maxWidth={600}
        className="cancellation-modal-context"
        footer={null}
      >
        <ReturnActionPanel
          booking={booking}
          onConfirm={handleReturnConfirm}
          onSkip={onClose}
          isSubmitting={isIssuingReturn}
          isGuest={isGuest}
          variant="base-modal"
        />
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title="Cancel This Booking?"
      icon={mdiCalendarRemove}
      iconColor="var(--dl-color-danger)"
      maxWidth={600}
      className="cancellation-modal-context"
      footer={
        <>
          <button
            className="base-modal-cancel-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Keep Booking
          </button>
          <button
            className="base-modal-save-btn cancellation-confirm-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
          </button>
        </>
      }
    >
      <div className="cancellation-warning">
        <Icon path={mdiAlert} size={1} className="cancellation-warning-icon" />
        <div className="cancellation-warning-content">
          <p className="cancellation-warning-title">
            Are you sure you want to cancel this booking?
          </p>
          <p className="cancellation-warning-text">
            This action will cancel the lesson and notify the student.
            If you just need to change the time, use the "Edit Time" option instead.
          </p>
        </div>
      </div>

      <div className="cancellation-booking-info">
        <p><strong>Student:</strong> {booking?.studentName || 'Unknown'}</p>
        <p><strong>Time:</strong> {booking?.timeSlot?.startTime} - {booking?.timeSlot?.endTime}</p>
        <p><strong>Date:</strong> {new Date(booking?.bookingDate).toLocaleDateString()}</p>
      </div>

      <div className="cancellation-reason-section">
        <label htmlFor="cancellation-reason" className="cancellation-reason-label">
          Cancellation Message
        </label>
        <p className="cancellation-form-hint">
          This message will be sent to the student. You can edit or send as written.
        </p>
        <textarea
          id="cancellation-reason"
          className="cancellation-reason-textarea"
          placeholder="Sorry, I need to cancel due to unforeseen circumstances..."
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
          rows={4}
          disabled={isSubmitting}
        />
      </div>
    </BaseModal>
  );
};

export default CancellationModal;
